import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { customerProjectPlanningAPI } from '../services/api';
import { CustomerPlanningProject } from '../types';
import TimeSelect from './TimeSelect';

const statusOptions = [
  { value: '', label: 'No status' },
  ...[
    'Preparing', 'Waiting for a response', 'Negotiation', 'Development', 'Testing',
    'Pilot', 'Follow-up', 'Post Implementation Review', 'Payment',
  ].map(status => ({ value: status, label: status })),
];

const CustomerProjectPlanningPage: React.FC = () => {
  const [projects, setProjects] = useState<CustomerPlanningProject[]>([]);
  const [showAll, setShowAll] = useState(false);
  const [nameFilter, setNameFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [savingIds, setSavingIds] = useState<Set<number>>(new Set());
  const savingRef = useRef(new Set<number>());
  const loadVersion = useRef(0);

  const loadProjects = async () => {
    const version = ++loadVersion.current;
    setLoading(true);
    setLoadError(false);
    try {
      const result = await customerProjectPlanningAPI.getProjects(true);
      if (version === loadVersion.current) setProjects(result);
    } catch {
      if (version === loadVersion.current) setLoadError(true);
    } finally {
      if (version === loadVersion.current) setLoading(false);
    }
  };

  useEffect(() => {
    loadProjects();
    return () => { loadVersion.current += 1; };
  }, []);

  const saveProject = async (projectId: number, change: Partial<CustomerPlanningProject>, save: () => Promise<void>, error: string) => {
    if (savingRef.current.has(projectId)) return;
    savingRef.current.add(projectId);
    setSavingIds(new Set(savingRef.current));
    try {
      await save();
      setProjects(current => current.map(project => project.projectId === projectId ? { ...project, ...change } : project));
      toast.success('Project planning updated');
    } catch {
      toast.error(error);
    } finally {
      savingRef.current.delete(projectId);
      setSavingIds(new Set(savingRef.current));
    }
  };

  const normalizedNameFilter = nameFilter.trim().toLowerCase();
  const visibleProjects = projects.filter(project =>
    (showAll || Boolean(project.planningStatus?.trim())) &&
    project.projectTitle.toLowerCase().includes(normalizedNameFilter));
  const customers = new Map<number, { title: string; projects: CustomerPlanningProject[] }>();
  visibleProjects.forEach(project => {
    const customer = customers.get(project.customerId) ?? { title: project.customerTitle, projects: [] };
    customer.projects.push(project);
    customers.set(project.customerId, customer);
  });

  return (
    <div className="fade-in">
      <div className="page-header">
        <div>
          <h1>Project planning</h1>
          <p>Plan projects by customer and track their progress.</p>
        </div>
        <label className="toggle-row planning-show-hidden-toggle">
          <input type="checkbox" checked={showAll} onChange={event => setShowAll(event.target.checked)} />
          Show all projects
        </label>
      </div>
      <p className="text-muted">Projects with a planning status appear here. Use “Show all projects” to find projects without a status.</p>
      <div className="filter-bar">
        <input
          type="search"
          className="form-control"
          aria-label="Filter by project name"
          placeholder="Filter by project name..."
          value={nameFilter}
          onChange={event => setNameFilter(event.target.value)}
        />
        {nameFilter && (
          <button type="button" className="btn btn-secondary" onClick={() => setNameFilter('')}>Clear filter</button>
        )}
      </div>
      {loading ? (
        <p role="status">Loading project planning...</p>
      ) : loadError ? (
        <div role="alert" className="card">
          <p>Project planning could not be loaded.</p>
          <button type="button" className="btn btn-secondary" onClick={loadProjects}>Retry</button>
        </div>
      ) : visibleProjects.length === 0 ? (
        <div className="card">
          <p>{normalizedNameFilter ? 'No projects match your filter.' : showAll ? 'No projects available.' : 'No projects have a planning status yet.'}</p>
        </div>
      ) : (
        <div className="customer-planning-tree" aria-label="Projects by customer">
          {Array.from(customers, ([customerId, customer]) => (
            <details key={customerId} open className="customer-planning-group card">
              <summary>
                <span>{customer.title}</span>
                <span className="customer-planning-count">{customer.projects.length} {customer.projects.length === 1 ? 'project' : 'projects'}</span>
              </summary>
              <ul className="customer-planning-projects">
                {customer.projects.map(project => {
                  const label = `${customer.title} / ${project.projectTitle}`;
                  const saving = savingIds.has(project.projectId);
                  return (
                    <li key={project.projectId} className="customer-planning-project" aria-busy={saving}>
                      <div className="customer-planning-project-name">
                        <strong>{project.projectTitle}</strong>
                        <span className="text-muted">{saving ? 'Saving...' : project.planningStatus?.trim() ? 'Included in plan' : 'No planning status'}</span>
                      </div>
                      <div className="form-group">
                        <label className="form-label" htmlFor={`planning-status-${project.projectId}`}>Planning status</label>
                        <TimeSelect<string>
                          inputId={`planning-status-${project.projectId}`}
                          ariaLabel={`Planning status: ${label}`}
                          options={statusOptions}
                          value={project.planningStatus ?? ''}
                          isDisabled={saving}
                          onChange={value => {
                            const planningStatus = value || null;
                            void saveProject(project.projectId, { planningStatus },
                              () => customerProjectPlanningAPI.updateStatus(project.projectId, planningStatus),
                              'Planning status could not be saved');
                          }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </details>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomerProjectPlanningPage;
